import React, {useEffect, useState} from 'react';

import moment from 'moment';
import styled from 'styled-components';

import {useCurrentUserState} from 'app/current-user/api';
import {usePreviewEmail} from 'app/notifly/api';
import {useAccountSettingsState} from 'app/settings/api';
import {gt} from 'app/utils/i18n';
import {markAsSafe} from 'app/utils/strings';

interface EmailPreviewProps {
  templateEmail?: string;
  templateName?: string;
  subject?: string;
  welcome_message?: string;
  setSubject: (subject: string) => unknown;
}

export default function EmailPreview({
  templateEmail,
  templateName,
  subject,
  welcome_message = gt(
    'You are invited to join SweetProcess, a new platform where you will be able to read our policy and procedure documentation.'
  ),
  setSubject,
}: EmailPreviewProps) {
  const {STATIC_URL} = useAccountSettingsState().data!;
  const {name, email} = useCurrentUserState().data!;

  const [template, setTemplate] = useState<string | undefined>();

  const [previewEmail, {data: previewData}] = usePreviewEmail();

  useEffect(() => {
    previewEmail({
      content_type: 'accountprofile',
      verb: 'invited',
      dynamic_data: {},
    });
  }, [previewEmail]);

  useEffect(() => {
    if (subject == null && previewData?.subject) {
      setSubject(previewData.subject);
    }
  }, [previewData?.subject]);

  useEffect(() => {
    let updated = previewData?.template ?? '';

    const values = {
      '{{ <a>action_object.from_user_full_name</a> }}': name,
      '{{ <a>user.profile.first_name_or_email</a> }}':
        templateName ?? templateEmail ?? '',
      '{{ None }}': welcome_message?.replace(/\n/g, '<br>'),
      '{{ STATIC_URL }}': STATIC_URL,
      '{{ SITE_DOMAIN }}': window.location.toString(),
      '{{ LOGO_LINK_URL }}': '',
    };

    Object.entries(values).forEach(([placeholder, replacement]) => {
      updated = updated.replace(placeholder, replacement);
    });
    setTemplate(updated);
  }, [templateName, templateEmail, subject, welcome_message]);

  return (
    <div className="col">
      <EmailHeader
        email={email}
        name={name}
        subject={subject}
        recipientName={templateName}
        recipientEmail={templateEmail}
      />
      <StyledEmailTemplateDiv
        dangerouslySetInnerHTML={markAsSafe(template ?? '')}
      />
    </div>
  );
}

interface EmailHeaderProps {
  name: string;
  email: string;
  recipientName?: string;
  recipientEmail?: string;
  subject?: string;
}

export function EmailHeader({
  name,
  email,
  subject,
  recipientName,
  recipientEmail,
}: EmailHeaderProps) {
  // Calculate the time difference from now to the email date
  const formattedDate = moment(new Date()).fromNow();

  return (
    <div className="border-bottom px-4 pt-1 pb-3">
      <div className="d-flex justify-content-between">
        <span>
          <span className="font-weight-bold mr-1">{name}</span>
          <span>&lt;{email}&gt;</span>
        </span>
        <span className="text-muted">
          {gt('Inbox')} &middot; {formattedDate}
        </span>
      </div>
      <div>{subject}</div>
      {recipientName ? (
        <div className="text-muted">
          {gt('To')}: {recipientName}{' '}
          {recipientEmail ? <span>&lt;{recipientEmail}&gt;</span> : null}
        </div>
      ) : null}
    </div>
  );
}

const StyledEmailTemplateDiv = styled.div`
  a {
    color: #333 !important;
  }
`;
